<script setup lang="ts">
import BackstageButton from '@/components/common/BackstageButton.vue';
import BackstageInput from '@/components/common/BackstageInput.vue';
import IconLogo from '@/components/icons/IconLogo.vue';
import { useAnalytics } from '@/composables/analytics';
import { useBPLogger } from '@/composables/business-process-logger';
import LoggedOutFrame from '@/layouts/LoggedOutFrame.vue';
import { GaCustomEvents } from '@/models/ga-custom-events.enum';
import { LoginService } from '@/services/login.service';
import { useUserStore } from '@/stores/user';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

const loginService = new LoginService();
const { t } = useI18n();

// refs
const username = ref('');
const password = ref('');
const isLoading = ref(false);
const errorMessage = ref<string | undefined>(undefined);

const isSubmitButtonDisabled = computed<boolean>(() => {
  return !username.value || !password.value || isLoading.value;
});

const salesRepLoginUrl = computed<string>(() => {
  return import.meta.env.VITE_APP_onBackstageAPI + 'login/oauth/redirect';
});

// methods
const login = async (event: Event) => {
  event.preventDefault();
  isLoading.value = true;
  errorMessage.value = undefined;
  const { pushAnalyticsEvent, setNewRelicUserId } = useAnalytics();
  const { setUserProfile, userProfile, selectedAccount, selectedAccountId } = useUserStore();
  const { loginUserBPLog } = useBPLogger();

  try {
    const loginModel = await loginService.login(username.value, password.value, selectedAccountId);

    if (loginModel) {
      setUserProfile(loginModel);
    } else {
      // ToDo show error
      return;
    }

    pushAnalyticsEvent({
      event: GaCustomEvents.Login,
      web_user_id: userProfile?.user.id,
      web_account_id: selectedAccount?.id,
      web_account_country: selectedAccount?.billingCountry,
    });

    setNewRelicUserId(loginModel.user.id);

    const params = new URL(document.location.href.replace('/#', '')).searchParams;
    const startURL = params.get('startURL') || '/';
    // Cache-bust the redirect to force the browser to fetch a fresh index.html from CloudFront.
    // This ensures the web-version cookie is evaluated and the correct UI version is served.
    const url = new URL(startURL, globalThis.location.origin);
    url.searchParams.set('_cb', Date.now().toString());
    globalThis.location.href = url.toString();

    loginUserBPLog(true);
  } catch (e: any) {
    if (e.message) {
      errorMessage.value = t(e.message);
      loginUserBPLog(false, { error_message: e.message });
    } else {
      errorMessage.value = t('backend.login.login_failed_unknown');
      loginUserBPLog(false, { error_message: errorMessage.value });
    }
  }

  isLoading.value = false;
};
</script>

<template>
  <LoggedOutFrame
    :background-image-url="$t('login.background_image_url')"
    :header="$t('login.header')"
    :show-account-message="true">
    <div class="login-form">
      <form class="form" v-on:submit="login">
        <div class="header">
          <IconLogo data-cy="logo" />
        </div>
        <div :v-if="errorMessage" class="error" data-cy="login-error-message">
          {{ errorMessage }}
        </div>

        <div class="form-fields">
          <BackstageInput
            v-model="username"
            name="username"
            :label="$t('login.username')"
            type="text"
            autocomplete="username"
            class="login-input"
            data-cy-input="username" />
          <BackstageInput
            v-model="password"
            name="password"
            :label="$t('login.password')"
            type="password"
            autocomplete="current-password"
            class="login-input"
            data-cy-input="password" />
        </div>

        <div class="links">
          <router-link to="/reset-password" data-cy="forgot-password-link" class="forgot-password-link">
            {{ $t('login.reset_password_link') }}
          </router-link>

          <a :href="salesRepLoginUrl" data-cy="sales-rep-login" class="sales-rep-login-link">
            {{ $t('login.sales_rep_login') }}
          </a>
        </div>

        <BackstageButton
          data-cy="login-button-cnt"
          data-cy-button="login-button"
          :class="[{ submitting: isLoading }]"
          :fullWidth="true"
          :disabled="isSubmitButtonDisabled">
          {{ $t('login.log_in_button') }}
        </BackstageButton>
      </form>
    </div>
  </LoggedOutFrame>
</template>

<style lang="scss" scoped>
.login-form {
  display: flex;
  flex-direction: column;
  padding-bottom: 1.5rem;

  .form {
    .header {
      margin-bottom: 2rem;

      :deep(svg) {
        display: flex;
        width: 16.5px;
      }
    }

    .form-fields {
      margin-bottom: 2rem;

      .login-input {
        display: flex;
        margin-bottom: 20px;
      }
    }

    .links {
      font-size: 1rem;
      display: flex;
      justify-content: space-between;
      margin-bottom: 2rem;

      .forgot-password-link {
        color: var(--primary-text-color);
        text-decoration: underline;
        display: block;
      }

      .sales-rep-login-link {
        color: var(--primary-text-color);
        text-decoration: underline;
      }

      .contact-link {
        color: var(--primary-light-color);
        text-decoration: underline;
      }
    }

    .submit-button {
      width: 100%;
      margin-bottom: 1.5rem;
    }

    .error {
      color: var(--scarlet-red-color);
      margin: 1rem 0;
    }
  }
}
</style>
